import React from 'react';
import { carouselControls, carouselControlButton, combineCarouselClasses } from './carousel.styles';
import type { CarouselControlsProps } from './types';

/**
 * CarouselControls - Navigation buttons for previous/next actions
 *
 * Provides accessible previous and next buttons for carousel navigation.
 * Automatically handles disabled states and provides proper ARIA labels.
 *
 * @example
 * ```tsx
 * <Carousel.Controls
 *   onPrevious={handlePrevious}
 *   onNext={handleNext}
 *   previousDisabled={isAtStart}
 *   nextDisabled={isAtEnd}
 * />
 * ```
 */
export const CarouselControls = React.forwardRef<HTMLDivElement, CarouselControlsProps>(
  (
    {
      onPrevious,
      onNext,
      previousDisabled = false,
      nextDisabled = false,
      previousLabel = 'Previous items',
      nextLabel = 'Next items',
      className,
      ...restProps
    },
    ref,
  ) => {
    const handlePreviousClick = () => {
      if (!previousDisabled && onPrevious) {
        onPrevious();
      }
    };

    const handleNextClick = () => {
      if (!nextDisabled && onNext) {
        onNext();
      }
    };

    const handlePreviousKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handlePreviousClick();
      }
    };

    const handleNextKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleNextClick();
      }
    };

    const combinedClassName = combineCarouselClasses(carouselControls, className);

    return (
      <div ref={ref} className={combinedClassName} {...restProps}>
        <button
          type="button"
          className={carouselControlButton}
          onClick={handlePreviousClick}
          onKeyDown={handlePreviousKeyDown}
          disabled={previousDisabled}
          aria-label={previousLabel}
          title={previousLabel}
        >
          <span aria-hidden="true">‹</span>
        </button>

        <button
          type="button"
          className={carouselControlButton}
          onClick={handleNextClick}
          onKeyDown={handleNextKeyDown}
          disabled={nextDisabled}
          aria-label={nextLabel}
          title={nextLabel}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>
    );
  },
);

CarouselControls.displayName = 'CarouselControls';
